import classNames from 'classnames'
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion'
import React, { PropsWithChildren, useCallback, useState } from 'react'

import { Body1, Title1 } from '@ui/Typography/Typography'
import { MinusIcon } from '@ui/icons/Minus.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'

import styles from './Accordion.module.scss'

export interface AccordionProps extends PropsWithChildren {
  expand?: boolean
  title: string
  leftAddon?: React.ReactNode
  description?: string
  fullWidth?: boolean
}

// TODO: add smooth opening
export const Accordion: React.FC<AccordionProps> = (props) => {
  const { expand, fullWidth, title, leftAddon, description, children } = props

  const [isExpanded, setIsExpanded] = useState<boolean>(!!expand)
  const reduceMotion = useReducedMotion()

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const transition: Transition = reduceMotion
    ? { duration: 0 }
    : { type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] as const }

  return (
    <div
      className={classNames(styles.accordion, {
        [styles.fullWidth]: fullWidth,
      })}
    >
      <div className={styles.header} onClick={handleToggle} onMouseDown={(e) => e.preventDefault()}>
        <div className={styles.headerSection}>
          {leftAddon && <div className={styles.leftAddon}>{leftAddon}</div>}
          <Title1 medium>{title}</Title1>
        </div>
        <div className={styles.icon}>{isExpanded ? <MinusIcon /> : <PlusIcon />}</div>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key='accordion-content'
            className={styles.content}
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: 'auto', opacity: 1, marginTop: 'var(--spacing-x16)' }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={transition}
          >
            <div className={styles.contentBody}>
              {description && <Body1 className={styles.description}>{description}</Body1>}
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
